import { useEffect, useMemo, useState } from "react";
import {
  loadBestBuyStores,
  loadCostcoStores,
  loadDollarGeneralStores,
  loadHomeDepotStores,
  loadLowesStores,
  loadTargetStores,
  loadWalmartStores,
} from "../utils/storesLoader";
import type { User } from "../services/UserService";
import {
  activeStoreRetailers,
  findInvalidStoresByRetailer,
  invalidStoresFingerprint,
} from "../utils/invalidStores";
import type { RetailerType } from "../config/retailers";

type StoreCatalogs = Partial<Record<RetailerType, unknown[]>>;

const DISMISS_KEY = "invalidStoresDismissed";

async function loadCatalog(retailer: RetailerType): Promise<unknown[]> {
  switch (retailer) {
    case "walmart":
      return loadWalmartStores();
    case "home_depot":
      return loadHomeDepotStores();
    case "dollar_general":
      return loadDollarGeneralStores();
    case "target":
      return loadTargetStores();
    case "bestbuy":
      return loadBestBuyStores();
    case "costco":
      return loadCostcoStores();
    case "lowes":
      return loadLowesStores();
    default:
      return [];
  }
}

export function useInvalidStores(user: User | null | undefined) {
  const [catalogs, setCatalogs] = useState<StoreCatalogs>({});
  const [loading, setLoading] = useState(false);
  const [dismissedFingerprint, setDismissedFingerprint] = useState<string | null>(
    () => sessionStorage.getItem(DISMISS_KEY)
  );

  const retailersToCheck = useMemo(
    () => (user ? activeStoreRetailers(user) : []),
    [user]
  );

  useEffect(() => {
    if (!user || retailersToCheck.length === 0) {
      setCatalogs({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      // A failed catalog fetch resolves to [] so one flaky network request
      // neither rejects unhandled nor discards the other retailers' checks;
      // findInvalidStoresByRetailer skips empty catalogs.
      const entries = await Promise.all(
        retailersToCheck.map(async (retailer) =>
          [retailer, await loadCatalog(retailer).catch(() => [])] as const
        )
      );
      if (cancelled) return;
      setCatalogs(Object.fromEntries(entries));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, retailersToCheck.join(",")]);

  const invalidByRetailer = useMemo(
    () => (user ? findInvalidStoresByRetailer(user, catalogs) : {}),
    [user, catalogs]
  );

  const fingerprint = useMemo(
    () => invalidStoresFingerprint(invalidByRetailer),
    [invalidByRetailer]
  );

  const hasInvalidStores = Object.keys(invalidByRetailer).length > 0;
  const isDismissed = !!fingerprint && dismissedFingerprint === fingerprint;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, fingerprint);
    setDismissedFingerprint(fingerprint);
  };

  return {
    invalidByRetailer,
    loading,
    hasInvalidStores,
    shouldShowModal: hasInvalidStores && !loading && !isDismissed,
    dismiss,
  };
}
